import React from 'react';
import ReactDOM from 'react-dom';
import './ScheduleGenerationConfigModal.scss';
import moment from 'moment';

const GENERATION_STEPS = [
  { threshold: 0,  message: 'Reading the demand forecast...' },
  { threshold: 15, message: "Checking who's available..." },
  { threshold: 35, message: 'Robot is packing lunches for everyone...' },
  { threshold: 50, message: 'Balancing shifts like a circus act...' },
  { threshold: 65, message: "Making sure no one works a double..." },
  { threshold: 80, message: 'Almost there, adding finishing touches...' },
  { threshold: 95, message: 'Wrapping it up...' },
];

class ScheduleGenerationConfirmModal extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      progress: 0,
      currentStep: 0,
    };
    this._progressInterval = null;
  }

  componentDidUpdate(prevProps) {
    // Start progress simulation when isGenerating flips to true
    if (this.props.isGenerating && !prevProps.isGenerating) {
      this.startProgressSimulation();
    }
    // Stop progress simulation if isGenerating flips to false (error case)
    if (!this.props.isGenerating && prevProps.isGenerating) {
      this.stopProgressSimulation();
    }
  }

  componentWillUnmount() {
    this.stopProgressSimulation();
  }

  /**
   * Estimate a speed multiplier based on location size and date range.
   * Small location + short range = fast; large location + long range = slow.
   */
  getSpeedMultiplier = () => {
    const { config, userCount = 0 } = this.props;
    const days = config.startDate && config.endDate
      ? Math.max(1, moment(config.endDate).diff(moment(config.startDate), 'days') + 1)
      : 7;

    // complexity score: users × days
    // ~20 users × 7 days = 140  → fast
    // ~50 users × 7 days = 350  → baseline
    // ~100 users × 14 days = 1400 → slow
    const complexity = (userCount || 30) * days;

    if (complexity < 150) return 0.5;   // ~12s to 95%  (small team, 1 week)
    if (complexity < 400) return 0.75;  // ~18s to 95%
    if (complexity < 800) return 1.0;   // ~25s to 95%  (baseline)
    return 1.4;                         // ~35s to 95%  (large team, 2 weeks)
  };

  startProgressSimulation = () => {
    this.setState({ progress: 0, currentStep: 0 });

    const speedMultiplier = this.getSpeedMultiplier();

    this._progressInterval = setInterval(() => {
      this.setState((prevState) => {
        let { progress } = prevState;

        // Base increments, scaled by speed multiplier
        // Lower multiplier = bigger increments = faster
        let baseIncrement;
        if (progress < 15) {
          baseIncrement = 0.35;
        } else if (progress < 35) {
          baseIncrement = 0.45;
        } else if (progress < 50) {
          baseIncrement = 0.35;
        } else if (progress < 65) {
          baseIncrement = 0.3;
        } else if (progress < 80) {
          baseIncrement = 0.25;
        } else if (progress < 95) {
          baseIncrement = 0.15;
        } else {
          baseIncrement = 0.03;
        }

        const increment = baseIncrement / speedMultiplier;
        const newProgress = Math.min(progress + increment, 99);

        // Determine current step based on threshold
        let newStep = 0;
        for (let i = GENERATION_STEPS.length - 1; i >= 0; i--) {
          if (newProgress >= GENERATION_STEPS[i].threshold) {
            newStep = i;
            break;
          }
        }

        return { progress: newProgress, currentStep: newStep };
      });
    }, 100);
  };

  stopProgressSimulation = () => {
    if (this._progressInterval) {
      clearInterval(this._progressInterval);
      this._progressInterval = null;
    }
    this.setState({ progress: 0, currentStep: 0 });
  };

  handleOverlayClick = (e) => {
    // Prevent closing the modal during generation
    if (this.props.isGenerating) {
      e.stopPropagation();
      return;
    }
    // Only close if clicking the overlay itself, not the modal content
    if (e.target === e.currentTarget) {
      this.props.onCancel();
    }
  };

  render() {
    const { config, onConfirm, onCancel, error, isGenerating } = this.props;
    const { progress, currentStep } = this.state;
    const {
      startDate,
      endDate,
      minShiftLength,
      maxShiftLength,
      minScheduleHoursPerWeek,
      overtimeThreshold,
      minBreakTime,
      availabilityFulfillLevel,
      useOpenShifts,
      hoursOfOperation
    } = config;

    const getAvailabilityText = (level) => {
      switch(level) {
        case 'always': return 'Always';
        case 'best_effort': return 'Best Effort';
        case 'never': return 'Never';
        default: return 'Best Effort';
      }
    };

    const formatHoursOfOp = (hours) => {
      if (!hours || typeof hours !== 'object') return 'Not configured';
      const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
      const parts = days.map(day => {
        const h = hours[day];
        if (!h || !h.start || !h.end) return null;
        const dayAbbr = day.slice(0, 3).toUpperCase();
        const start = moment(h.start).format('h:mmA');
        const end = moment(h.end).format('h:mmA');
        return `${dayAbbr} ${start}-${end}`;
      }).filter(Boolean);
      return parts.length > 0 ? parts.join(', ') : 'Not configured';
    };

    const configItems = [
      { label: 'Date Range', value: `${moment(startDate).format('MMM D, YYYY')} — ${moment(endDate).format('MMM D, YYYY')}` },
      { label: 'Shift Length', value: `${minShiftLength?.hours || 0}h ${minShiftLength?.minutes || 0}m – ${maxShiftLength?.hours || 0}h ${maxShiftLength?.minutes || 0}m` },
      { label: 'Min Hours/Week', value: `${minScheduleHoursPerWeek || 0} hours` },
      { label: 'Overtime Threshold', value: `${overtimeThreshold || 0} hours` },
      { label: 'Rest Period', value: `${(minBreakTime || 0) / 60} hours` },
      { label: 'Availability', value: getAvailabilityText(availabilityFulfillLevel) },
      { label: 'Open Shifts', value: useOpenShifts ? 'Enabled' : 'Disabled' },
      { label: 'Hours of Operation', value: formatHoursOfOp(hoursOfOperation) },
    ];

    const stepMessage = GENERATION_STEPS[currentStep]?.message || '';

    return ReactDOM.createPortal(
      <div className="confirm-modal-overlay" onClick={this.handleOverlayClick}>
        <div className="confirm-modal">
          <div className="confirm-modal-content">
            <h3>Confirm Generation</h3>
            <p>Review your configuration before generating.</p>
            <div className="config-summary">
              {configItems.map((item, index) => (
                <div key={index} className="config-summary-row">
                  <span className="config-summary-label">{item.label}</span>
                  <span className="config-summary-value">{item.value}</span>
                </div>
              ))}
            </div>
            {error && (
              <div className="gen-error-message">
                <img src="icons/error.svg" alt="Error" className="error-icon" />
                <span>{error}</span>
              </div>
            )}

            {/* Footer: show buttons or inline progress */}
            {isGenerating ? (
              <div className="confirm-modal-generating">
                <div className="generating-progress-row">
                  <div className="generating-progress-bar-container">
                    <div
                      className="generating-progress-bar-fill"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                  <div className="generating-spinner">
                    <div className="generating-spinner-circle" />
                  </div>
                </div>
                <div className="generating-step-message">{stepMessage}</div>
              </div>
            ) : (
              <div className="confirm-modal-buttons">
                <button
                  className="cancel-button"
                  onClick={onCancel}
                >
                  Cancel
                </button>
                <button
                  className="confirm-button"
                  onClick={onConfirm}
                >
                  Confirm
                </button>
              </div>
            )}
          </div>
        </div>
      </div>,
      document.body
    );
  }
}

export default ScheduleGenerationConfirmModal;
